import React, {useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import PaymentMethodsModal from '../pages/UserPages/accounts/Billing/PaymentMethodsModal'
import {
  PaymentMethodCard,
  PreviewType,
  ProductPlan,
  ProductPlanTier,
  Subscription,
  TaxRate,
} from '../../services/cellcast.v2.types'
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import { loadStripe, StripeCardElement } from '@stripe/stripe-js'
import { ENV } from '../Utils/_helpers/EnvHelper'
import {
  usePurchaseProductPlanMutation,
  useGetTrialStatusQuery,
  useGetUpcomingInvoiceChangePlanQuery,
  useChangePlanMutation,
} from '../../services/cellcast.v2'
import { useGetPaymentMethodsQuery } from '../../services/cellcast.v2'
import { toast } from 'react-toastify'
import { getFutureBillingDate } from '../Utils/Constant/formatDayTime'
import './Style/PlanPaymentForm.scss'

import { formatMoney } from '../pages/UserPages/contact-collection/utils/money'
import { TRIAL_SUBSCRIPTION_DAYS } from '../Utils/Constant/subs'

const stripePromise = (() => {
  const publicKey = ENV().REACT_APP_PUBLIC_KEY
  if (!publicKey || publicKey === 'pk_test_placeholder') {
    return null
  }
  return loadStripe(publicKey)
})()

type PlanPaymentFormProps = {
  show: boolean
  onClose: () => void
  plan: ProductPlan | null
  onPaymentSuccess: () => void
  applyNow?: boolean
  isUpdate?: boolean
  subscription?: Subscription
  currentPlan?: ProductPlan | null
  previewType?: PreviewType
}

const PlanPaymentForm: React.FC<PlanPaymentFormProps> = ({
  show,
  onClose,
  plan,
  onPaymentSuccess,
  applyNow,
  isUpdate,
  currentPlan,
  previewType,
  subscription,
}) => {
  if (!show || !plan) return null

  // Check if plan is free
  const isFreePlan = (plan.price === 0 || plan.planKey === ProductPlanTier.FREE)

  // Don't render if Stripe is not properly configured (only for paid plans)
  if (!stripePromise && !isFreePlan) {
    return (
      <Modal
        show={show}
        onHide={onClose}
        dialogClassName='modal-dialog-centered'
        backdrop='static'
        keyboard={false}
        centered
        size='lg'
      >
        <Modal.Header closeButton className='border-0'>
          <h2 className='title'>Payment Configuration Error</h2>
        </Modal.Header>
        <Modal.Body>
          <div className='alert alert-warning'>
            <strong>Payment system not configured.</strong> Please contact support to set up payment processing.
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      dialogClassName='modal-dialog-centered'
      backdrop='static'
      keyboard={false}
      centered
      size='lg'
    >
      {stripePromise ? (
        <Elements stripe={stripePromise}>
          <PaymentFormContent
            onClose={onClose}
            plan={plan}
            onPaymentSuccess={onPaymentSuccess}
            applyNow={applyNow}
            isUpdate={isUpdate}
            currentPlan={currentPlan}
            previewType={previewType}
            subscription={subscription}
            isFreePlan={isFreePlan}
          />
        </Elements>
      ) : isFreePlan ? (

        <FreePlanActivation
          onClose={onClose}
          plan={plan}
          onPaymentSuccess={onPaymentSuccess}
          isUpdate={isUpdate}
          subscription={subscription}
        />
      ) : (
        <div className='payment-form-container'>
          <Modal.Header closeButton className='border-0'>
            <h2 className='title'>Payment Configuration Error</h2>
          </Modal.Header>
          <Modal.Body>
            <div className='alert alert-warning'>
              <strong>Payment system not configured.</strong> Please contact support to set up
              payment processing.
            </div>
          </Modal.Body>
        </div>
      )}
    </Modal>
  )
}

// Component for free plans that don't require Stripe
const FreePlanActivation: React.FC<{
  onClose: () => void
  plan: ProductPlan
  onPaymentSuccess: () => void
  isUpdate?: boolean
  subscription?: Subscription
}> = ({ onClose, plan, onPaymentSuccess, isUpdate, subscription }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [purchaseProductPlan] = usePurchaseProductPlanMutation()
  const [updateProductPLan] = useChangePlanMutation()

  const extractApiErrorMessage = (resp: any, fallback: string) => {
    try {
      const data: any = resp?.error?.data ?? resp?.data ?? resp
      if (data?.error?.message) return String(data.error.message)
      if (data?.message) return String(data.message)
      if (resp?.error?.message) return String(resp.error.message)
    } catch (_) { }
    return fallback
  }

  const handleActivate = async () => {
    setIsSubmitting(true)
    setPaymentError('')
    try {
      if (isUpdate) {
        const resp = await updateProductPLan({
          subscriptionId: subscription?._id || '',
          planId: plan._id,
          applyNow: false,
        })

        if ('data' in resp && resp.data) {
          const apiResponse = resp.data
          const successMessage = apiResponse?.message || 'Subscription updated successfully!'
          toast.success(successMessage)
          onPaymentSuccess()
          return
        } else if ('error' in resp) {
          const errorMessage = extractApiErrorMessage(
            resp.error,
            'Failed to update subscription plan'
          )
          throw new Error(errorMessage)
        } else {
          throw new Error('Unexpected response format')
        }
      }

      // For free plans, call API without card or token
      const resp = await purchaseProductPlan({
        productId: plan.productId._id,
        planId: plan._id,
        priceId: plan.stripePriceId || undefined,
      })

      if ('data' in resp && resp.data) {
        const apiResponse = resp.data
        const successMessage = apiResponse?.message || 'Free plan activated successfully!'
        toast.success(successMessage)
        onPaymentSuccess()
      } else if ('error' in resp) {
        const errorMessage = extractApiErrorMessage(resp.error, 'Failed to activate free plan')
        throw new Error(errorMessage)
      } else {
        throw new Error('Unexpected response format')
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'An unexpected error occurred'
      setPaymentError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='payment-form-container'>
      <Modal.Header closeButton className='border-0'>
        <div className='w-100'>
          <h2 className='title'>
            {isUpdate ? `Switch to ${plan.name}` : `Activate ${plan.name}`}
          </h2>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className='plan-card'>
          <div className='d-flex align-items-center justify-content-between'>
            <div>
              <div className='plan-name'>{plan.name}</div>
              <div className='plan-features'>{plan.features.join(' • ')}</div>
            </div>
            <div className='d-flex align-items-center gap-3'>
              <div className='text-end'>
                <div className='plan-price'>
                  ${plan.price} / {plan.billingInterval}
                </div>
              </div>
              <span className='selected-badge'>Selected</span>
            </div>
          </div>
        </div>

        <div className='trial-notice mb-4'>
          <div>
            <strong>Free plan.</strong> No credit card required. Activate now and start using the
            plan immediately.
          </div>
        </div>

        {paymentError && <div className='error-alert mb-4'>{paymentError}</div>}

        <div className='pricing-summary'>
          <div className='summary-row'>
            <span className='label'>Plan Price</span>
            <span className='value large'>Free</span>
          </div>
        </div>

        <button
          className='btn btn-primary'
          onClick={(e) => {
            e.preventDefault()
            handleActivate()
          }}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span
                className='spinner-border spinner-border-sm spinner'
                role='status'
                aria-hidden='true'
              ></span>
              Processing...
            </>
          ) : isUpdate ? (
            `Switch to ${plan.name}`
          ) : (
            `Activate ${plan.name}`
          )}
        </button>

        <div className='security-notice'>
          No payment required • Cancel anytime • 24/7 support
        </div>
      </Modal.Body>
    </div>
  )
}

interface PaymentFormContentProps {
  onClose: () => void
  plan: ProductPlan
  onPaymentSuccess: () => void
  applyNow?: boolean
  isUpdate?: boolean
  currentPlan?: ProductPlan | null
  previewType?: PreviewType
  subscription?: Subscription
  isFreePlan?: boolean
}

const PaymentFormContent: React.FC<PaymentFormContentProps> = ({
  onClose,
  plan,
  onPaymentSuccess,
  applyNow,
  isUpdate,
  currentPlan,
  previewType,
  subscription,
  isFreePlan = false,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [processingAction, setProcessingAction] = useState<'upgrade' | 'trial' | null>(null)
  const [showPaymentMethods, setShowPaymentMethods] = useState(false)

  // Determine if plan is free (fallback check if isFreePlan prop not provided)
  const planIsFree = isFreePlan || plan.price === 0 || plan.planKey === ProductPlanTier.FREE

  const {
    data: pmResponse,
    refetch: refetchPaymentMethods,
    isLoading: isLoadingPM,
  } = useGetPaymentMethodsQuery(null, { skip: planIsFree })
  const paymentMethods: PaymentMethodCard[] = pmResponse?.data || []
  const defaultMethod = paymentMethods[0]
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)

  useEffect(() => {
    if (!planIsFree) {
      setSelectedCardId(defaultMethod?.id || null)
    }
  }, [defaultMethod?.id, planIsFree])

  const handleGetCards = useCallback(() => {
    setShowPaymentMethods(true)
  }, [])

  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [purchaseProductPlan] = usePurchaseProductPlanMutation()
  const [updateProductPLan] = useChangePlanMutation()
  const {
    data: upcomingInvoice,
    isLoading: isLoadingInvoice,
    error: invoiceError,
  } = useGetUpcomingInvoiceChangePlanQuery({
    planId: plan._id,
    applyNow: applyNow || false,
    previewType: previewType || PreviewType.new_subscription,
    currentPlanId: currentPlan?._id || null,
    newPlanId: plan._id,
    isTrial: subscription?.status === 'trialing',
  })

  const { data: trialStatusResp } = useGetTrialStatusQuery(
    { productId: plan.productId._id },
    { skip: !plan?.productId?._id }
  )

  const canStartTrial = trialStatusResp?.data?.trialStatus?.canStartTrial

  const invoiceData = upcomingInvoice?.data

  const pricing = invoiceData?.changePreview?.pricing

  const billingDateString = getFutureBillingDate(TRIAL_SUBSCRIPTION_DAYS)

  const extractApiErrorMessage = (resp: any, fallback: string) => {
    try {
      const data: any = resp?.error?.data ?? resp?.data ?? resp
      if (data?.error?.message) return String(data.error.message)
      if (data?.message) return String(data.message)
      if (resp?.error?.message) return String(resp.error.message)
    } catch (_) { }
    return fallback
  }

  const handleError = (error: any, context: string) => {
    console.error(`${context}:`, error)

    let errorMessage = 'An unexpected error occurred'

    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error?.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message
    } else if (error?.message) {
      errorMessage = error.message
    } else if (typeof error === 'string') {
      errorMessage = error
    }

    setPaymentError(errorMessage)
    toast.error(errorMessage)
  }

  const handleUpgrade = async (trial: boolean = false) => {
    setIsSubmitting(true)
    setProcessingAction(trial ? 'trial' : 'upgrade')
    setPaymentError('')
    try {
      // Handle free plans - no payment required
      if (planIsFree) {
        if (isUpdate) {
          const resp = await updateProductPLan({
            subscriptionId: subscription?._id || '',
            planId: plan._id,
            applyNow: applyNow || false,
          })

          if ('data' in resp && resp.data) {
            const apiResponse = resp.data
            const successMessage = apiResponse?.message || 'Subscription updated successfully!'
            toast.success(successMessage)
            onPaymentSuccess()
            return
          } else if ('error' in resp) {
            const errorMessage = extractApiErrorMessage(
              resp.error,
              'Failed to update subscription plan'
            )
            throw new Error(errorMessage)
          } else {
            throw new Error('Unexpected response format')
          }
        }

        // For free plans, call API without card or token
        const resp = await purchaseProductPlan({
          productId: plan.productId._id,
          planId: plan._id,
          priceId: plan.stripePriceId || undefined,
          isTrial: trial,
        })

        if ('data' in resp && resp.data) {
          const apiResponse = resp.data
          const successMessage = apiResponse?.message || 'Free plan activated successfully!'
          toast.success(successMessage)
          onPaymentSuccess()
          return
        } else if ('error' in resp) {
          const errorMessage = extractApiErrorMessage(
            resp.error,
            'Failed to activate free plan'
          )
          throw new Error(errorMessage)
        } else {
          throw new Error('Unexpected response format')
        }
      }

      // Handle paid plans - require payment
      if (!stripe || !elements) {
        throw new Error('Payment system not initialized. Please refresh the page and try again.')
      }

      if (selectedCardId) {
        if (isUpdate) {
          const resp = await updateProductPLan({
            subscriptionId: subscription?._id || '',
            planId: plan._id,
            applyNow: applyNow || false,
          })

          if ('data' in resp && resp.data) {
            const apiResponse = resp.data

            const successMessage = apiResponse?.message || 'Subscription updated successfully!'
            toast.success(successMessage)
            onPaymentSuccess()
            return
          } else if ('error' in resp) {
            const errorMessage = extractApiErrorMessage(
              resp.error,
              'Failed to update subscription plan'
            )
            throw new Error(errorMessage)
          } else {
            throw new Error('Unexpected response format')
          }
        }

        const resp = await purchaseProductPlan({
          productId: plan.productId._id,
          planId: plan._id,
          priceId: plan.stripePriceId || undefined,
          card: selectedCardId,
          isTrial: trial,
        })

        if ('data' in resp && resp.data) {
          const apiResponse = resp.data

          const successMessage = apiResponse?.message || 'Subscription created successfully!'
          toast.success(successMessage)
          onPaymentSuccess()
        } else if ('error' in resp) {
          const errorMessage = extractApiErrorMessage(
            resp.error,
            'Failed to purchase subscription plan'
          )
          throw new Error(errorMessage)
        } else {
          throw new Error('Unexpected response format')
        }
      } else {
        const cardElement = elements.getElement(CardNumberElement)
        if (!cardElement) {
          throw new Error('Payment form not ready. Please check your card details and try again.')
        }

        const { token, error } = await stripe.createToken(
          cardElement as unknown as StripeCardElement,
          { name: 'Business Card Subscription' }
        )

        if (error) {
          if (error.code === 'card_declined') {
            throw new Error('Your card was declined. Please try a different payment method.')
          } else if (error.code === 'expired_card') {
            throw new Error('Your card has expired. Please use a different payment method.')
          } else if (error.code === 'incorrect_cvc') {
            throw new Error('The CVC code you entered is incorrect. Please check and try again.')
          } else if (error.code === 'processing_error') {
            throw new Error('There was an error processing your card. Please try again.')
          } else {
            throw new Error(error.message || 'Card validation failed. Please check your details.')
          }
        }

        if (!token) {
          throw new Error('Failed to process payment card. Please try again.')
        }

        const resp = await purchaseProductPlan({
          productId: plan.productId._id,
          planId: plan._id,
          priceId: plan.stripePriceId || undefined,
          token: token.id,
          isTrial: trial,
        })

        if ('data' in resp && resp.data) {
          const apiResponse = resp.data

          const successMessage = apiResponse?.message || 'Subscription created successfully!'
          toast.success(successMessage)
          onPaymentSuccess()
        } else if ('error' in resp) {
          const errorMessage = extractApiErrorMessage(resp.error, 'Failed to create subscription')
          throw new Error(errorMessage)
        } else {
          throw new Error('Unexpected response format')
        }
      }
    } catch (error: any) {
      handleError(error, 'Payment processing error')
    } finally {
      setIsSubmitting(false)
      setProcessingAction(null)
    }
  }

  const getTitlePaymentForm = () => {
    if (planIsFree) {
      if (isUpdate) {
        return `Switch to ${plan.name}`
      }
      return `Activate ${plan.name}`
    }

    if (isUpdate) {
      if (subscription?.status === 'trialing') {
        return `Upgrade from Trial to ${plan.name}`
      }
      return `Update to ${plan.name}`
    }

    if (canStartTrial && !isUpdate) {
      return `Start ${plan.name} Free Trial`
    }

    return `Activate ${plan.name}`
  }

  return (
    <div className='payment-form-container'>
      <Modal.Header closeButton className='border-0'>
        <div className='w-100'>
          <h2 className='title'>{getTitlePaymentForm()}</h2>
          {!planIsFree && (
            <div className='security-info d-flex gap-2'>
              <span className='d-flex align-items-center gap-1'>
                🔒 <span>Secure checkout</span>
              </span>
              <span>•</span>
              <span>
                Powered by <strong>Stripe</strong>
              </span>
            </div>
          )}
          {!planIsFree && (
            <div className='mt-3 d-flex gap-2'>
              <button type='button' className='btn btn-primary btn-sm' onClick={handleGetCards}>
                Get cards
              </button>
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        {!planIsFree && (
          <PaymentMethodsModal
            show={showPaymentMethods}
            onClose={() => {
              setShowPaymentMethods(false)
              refetchPaymentMethods()
            }}
            stripePublishableKey={ENV().REACT_APP_PUBLIC_KEY || 'pk_test_placeholder'}
            onSelect={(id) => {
              setSelectedCardId(id)
              setShowPaymentMethods(false)
            }}
          />
        )}

        <div className='plan-card'>
          <div className='d-flex align-items-center justify-content-between'>
            <div>
              <div className='plan-name'>{plan.name}</div>
              <div className='plan-features'>{plan.features.join(' • ')}</div>
            </div>
            <div className='d-flex align-items-center gap-3'>
              <div className='text-end'>
                <div className='plan-price'>
                  ${plan.price} / {plan.billingInterval}
                </div>
              </div>
              <span className='selected-badge'>Selected</span>
            </div>
          </div>
        </div>

        {planIsFree && (
          <div className='trial-notice mb-4'>
            <div>
              <strong>Free plan.</strong> No credit card required. Activate now and start using the plan immediately.
            </div>
          </div>
        )}

        {(canStartTrial || (isUpdate && subscription?.status === 'trialing')) && !planIsFree && (
          <div className='trial-notice'>
            <div>
              {isUpdate && subscription?.status === 'trialing' ? (
                <>
                  <strong>Upgrading from trial.</strong> You won't be charged today. Your trial
                  period continues. Billing will start when your trial ends.
                </>
              ) : (
                <>
                  <strong>{TRIAL_SUBSCRIPTION_DAYS}-day free trial.</strong> You won't be charged today. Cancel anytime.
                  Billing starts on <strong>{billingDateString}</strong>.
                </>
              )}
            </div>
          </div>
        )}

        {!planIsFree && (
          <div className='card-form mb-4'>
            {isLoadingPM ? (
              <div className='d-flex align-items-center gap-2 text-muted'>
                <span className='spinner-border spinner-border-sm'></span>
                Loading saved cards...
              </div>
            ) : selectedCardId ? (
              <div className='d-flex justify-content-between align-items-center p-3 border rounded-3 mb-3'>
                <div className='fw-semibold'>Using saved card ending **** {defaultMethod?.last4}</div>
                <button className='btn btn-sm btn-light-primary' onClick={handleGetCards}>
                  Change
                </button>
              </div>
            ) : (
              <div className='mb-3'>
                <div className='card-input d-flex align-items-center gap-2'>
                  <span className='card-icon'>💳</span>
                  <div className='flex-grow-1'>
                    <CardNumberElement
                      options={{
                        style: {
                          base: {
                            fontSize: '14px',
                            color: '#374151',
                            fontFamily:
                              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            '::placeholder': {
                              color: '#9ca3af',
                            },
                          },
                          invalid: {
                            color: '#ef4444',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {!selectedCardId && (
              <div className='row g-3 mb-3'>
                <div className='col-6'>
                  <div className='card-input'>
                    <CardExpiryElement
                      options={{
                        style: {
                          base: {
                            fontSize: '14px',
                            color: '#374151',
                            fontFamily:
                              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            '::placeholder': {
                              color: '#9ca3af',
                            },
                          },
                          invalid: {
                            color: '#ef4444',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <div className='col-6'>
                  <div className='card-input'>
                    <CardCvcElement
                      options={{
                        style: {
                          base: {
                            fontSize: '14px',
                            color: '#374151',
                            fontFamily:
                              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            '::placeholder': {
                              color: '#9ca3af',
                            },
                          },
                          invalid: {
                            color: '#ef4444',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className='card-brands d-flex align-items-center gap-2'>
              <span className='brand-badge'>Visa</span>
              <span className='brand-badge'>Mastercard</span>
              <span className='brand-badge'>Amex</span>
            </div>
          </div>
        )}

        {paymentError && <div className='error-alert mb-4'>{paymentError}</div>}

        {!planIsFree && invoiceError && (
          <div className='error-alert mb-4'>
            Failed to load pricing information. Please try again.
          </div>
        )}

        {!planIsFree && (
          <div className='pricing-summary'>
            {isLoadingInvoice ? (
              <>
                <div className='summary-row'>
                  <div className='loading-skeleton medium'></div>
                  <div className='loading-skeleton small'></div>
                </div>
                <div className='summary-row'>
                  <div className='loading-skeleton medium'></div>
                  <div className='loading-skeleton small'></div>
                </div>
                <div className='divider'></div>
                <div className='summary-row'>
                  <div className='loading-skeleton medium'></div>
                  <div className='loading-skeleton wide'></div>
                </div>
              </>
            ) : (
              invoiceData?.changePreview && (
                <>
                  <div className='summary-row'>
                    <span className='label'>Subtotal</span>
                    <span className='value medium'>
                      {formatMoney(pricing?.subtotal, pricing?.currency)}
                    </span>
                  </div>

                  <hr className='divider' />

                  {invoiceData.changePreview.taxRates?.map((taxRate: TaxRate, index: number) => (
                    <div key={taxRate.id || index} className='summary-row'>
                      <span className='label'>
                        {taxRate.displayName} ({taxRate.percentage}%)
                      </span>
                      <span className='value medium'>
                        {formatMoney(pricing?.tax, pricing?.currency)}
                      </span>
                    </div>
                  ))}

                  <hr className='divider' />
                  <div className='summary-row'>
                    <span className='label'>
                      {canStartTrial && !(isUpdate && subscription?.status === 'trialing')
                        ? 'After trial'
                        : isUpdate && subscription?.status === 'trialing'
                          ? 'Upgrade Total'
                          : 'Total'}
                    </span>
                    <div className='text-end'>
                      <div className='value large'>
                        {formatMoney(pricing?.total, pricing?.currency)}
                      </div>
                      {canStartTrial && !(isUpdate && subscription?.status === 'trialing') && (
                        <div className='sub-text'>First charge on {billingDateString}</div>
                      )}
                      {isUpdate && subscription?.status === 'trialing' && (
                        <div className='sub-text'>Charged when trial ends</div>
                      )}
                    </div>
                  </div>
                </>
              )
            )}
          </div>
        )}

        {planIsFree && (
          <div className='pricing-summary'>
            <div className='summary-row'>
              <span className='label'>Plan Price</span>
              <span className='value large'>Free</span>
            </div>
          </div>
        )}

        <button
          className='btn btn-primary'
          onClick={(e) => {
            e.preventDefault()
            const isTrialingUser = isUpdate && subscription?.status === 'trialing'
            handleUpgrade(canStartTrial && !isTrialingUser && !planIsFree)
          }}
          disabled={isSubmitting || (!planIsFree && (!stripe || isLoadingInvoice))}
        >
          {isSubmitting &&
            processingAction ===
            (canStartTrial && !(isUpdate && subscription?.status === 'trialing') && !planIsFree
              ? 'trial'
              : 'upgrade') ? (
            <>
              <span
                className='spinner-border spinner-border-sm spinner'
                role='status'
                aria-hidden='true'
              ></span>
              Processing...
            </>
          ) : !planIsFree && isLoadingInvoice ? (
            'Loading pricing...'
          ) : planIsFree ? (
            isUpdate ? (
              `Switch to ${plan.name}`
            ) : (
              `Activate ${plan.name}`
            )
          ) : isUpdate && subscription?.status === 'trialing' ? (
            `Upgrade to ${plan.name}`
          ) : canStartTrial ? (
            `Start ${TRIAL_SUBSCRIPTION_DAYS}-Day Free Trial`
          ) : (
            `Subscribe to ${plan.name}`
          )}
        </button>

        {!planIsFree && (
          <div className='security-notice'>
            Encrypted payments by Stripe • Cancel anytime • 24/7 support
          </div>
        )}
        {planIsFree && (
          <div className='security-notice'>
            No payment required • Cancel anytime • 24/7 support
          </div>
        )}
      </Modal.Body>
    </div>
  )
}

export default PlanPaymentForm
